'use client';

import {
  Alert,
  Anchor,
  Grid,
  Group,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import PlaceAutocompleteElement from 'react-google-autocomplete';

import { TrackedButton, Warning } from '@/components';
import { useFooter } from '@/contexts';
import { useAuth } from '@/hooks';
import { logger } from '@/utils/logger';

import styles from './VoterVerificationForm.module.css';

interface VoterVerificationFormProps {
  onSubmit?: (formData: VoterVerificationData) => void;
  onCancel?: () => void;
  hideTitle?: boolean;
  personId: string;
  onVerificationSuccess?: () => void;
}

export interface VoterVerificationData {
  address?: string;
  state: string;
  latitude?: number;
  longitude?: number;
  sendTextUpdates?: boolean;
  firstName: string;
  lastName: string;
  dob?: string;
}

interface VoterInfo {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  registrationStatus: string;
  voterId: string;
}

interface VerificationResult {
  isVerified: boolean;
  voterInfo?: VoterInfo;
  geoIds?: string[];
}

export function VoterVerificationForm({
  onSubmit,
  onCancel,
  hideTitle = false,
  personId,
  onVerificationSuccess,
}: VoterVerificationFormProps) {
  const [formData, setFormData] = useState<VoterVerificationData>({
    sendTextUpdates: false,
    firstName: '',
    lastName: '',
    address: '',
    state: '',
    latitude: 0,
    longitude: 0,
  });
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [verificationResult, setVerificationResult] =
    useState<VerificationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDobField, setShowDobField] = useState(false);
  const { refetchUser } = useAuth();

  const buttonPrimaryText = verificationResult?.isVerified
    ? 'BACK TO PROFILE'
    : 'VERIFY';
  const buttonSecondaryText = 'CANCEL';
  const formRef = useRef<HTMLFormElement>(null);
  const {
    buttonPrimaryProps,
    buttonSecondaryProps,
    setButtonPrimaryProps,
    setButtonSecondaryProps,
    clearButtonPrimaryProps,
    clearButtonSecondaryProps,
  } = useFooter();

  const googleApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!googleApiKey) {
      setError(
        'Google API key is not configured. Please set NEXT_PUBLIC_GOOGLE_MAPS_API_KEY.',
      );
      setIsLoading(false);
    }
  }, [googleApiKey]);

  useEffect(() => {
    if (verificationResult?.isVerified) {
      refetchUser(); // For Auth purposes

      if (onVerificationSuccess) {
        onVerificationSuccess();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationResult, onVerificationSuccess]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear previous verification results when form is modified
    setVerificationResult(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    logger.info(
      'VoterVerificationForm: handleSubmit called at ' +
        new Date().toISOString(),
    );
    e.preventDefault();

    if (onSubmit) {
      onSubmit(formData);
    }

    if (!isAddressSelected) {
      setError(
        'Please search for an address and select one from the dropdown.',
      );
      return;
    }

    if (
      (formData.latitude == null || formData.longitude == null) &&
      !formData.address
    ) {
      setError('Address is invalid');
      return;
    }

    setIsLoading(true);
    setError(null);
    setVerificationResult(null);

    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
    if (!backendUrl) {
      setError(
        'Backend URL is not configured. Please set NEXT_PUBLIC_BACKEND_URL.',
      );
      setIsLoading(false);
      return;
    }

    try {
      // Convert MM/DD/YYYY to YYYYMMDD for backend
      let dobForBackend = '';
      if (formData.dob) {
        const match = formData.dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (match) {
          const [, mm, dd, yyyy] = match;
          dobForBackend = `${yyyy}${mm}${dd}`;
        } else {
          setError('Date of birth must be in MM/DD/YYYY format');
          setIsLoading(false);
          return;
        }
      }

      const requestBody = {
        person_id: personId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        full_address: formData.address || '',
        state: formData.state,
        latitude: formData.latitude?.toString() || '',
        longitude: formData.longitude?.toString() || '',
        dob: dobForBackend,
      };

      const response = await fetch(`${backendUrl}/api/verifyVoter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Voter verification failed');
      }

      const data = await response.json();

      if (
        data.status === 'success' &&
        data.data.result === true &&
        data.data.result_set?.length > 0
      ) {
        const verificationResult = data.data.result_set[0];

        const voterInfo: VoterInfo = {
          firstName: verificationResult['vb.tsmart_first_name'],
          middleName: verificationResult['vb.tsmart_middle_name'],
          lastName: verificationResult['vb.tsmart_last_name'],
          suffix: verificationResult['vb.tsmart_name_suffix'],
          registrationStatus:
            verificationResult['vb.voterbase_registration_status'],
          voterId: verificationResult['vb.voterbase_id'],
        };

        setVerificationResult({
          isVerified: true,
          voterInfo,
        });

        setShowDobField(false);
      }
    } catch {
      setVerificationResult({ isVerified: false });
      setShowDobField(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setButtonPrimaryProps({
      children: buttonPrimaryText,
      variant: 'primary',

      trackingName: verificationResult?.isVerified
        ? 'VoterVerificationForm_back_to_profile_button'
        : 'VoterVerificationForm_verify_button',
      onClick: verificationResult?.isVerified
        ? undefined
        : () => formRef?.current?.requestSubmit(),
      renderRoot: verificationResult?.isVerified
        ? (props) => (
            <Link
              href="/me"
              {...props}
            />
          )
        : undefined,
      loading: isLoading,
    });

    if (onCancel) {
      setButtonSecondaryProps({
        children: buttonSecondaryText,
        variant: 'outline',
        onClick: onCancel,
        trackingName: 'VoterVerificationForm_cancel_button',
      });
    } else {
      clearButtonSecondaryProps();
    }

    return () => {
      clearButtonPrimaryProps();
      clearButtonSecondaryProps();
    };
  }, [
    isLoading,
    buttonPrimaryText,
    buttonSecondaryText,
    verificationResult?.isVerified,
    setButtonPrimaryProps,
    setButtonSecondaryProps,
    clearButtonPrimaryProps,
    clearButtonSecondaryProps,
    onCancel,
  ]);

  if (!googleApiKey) {
    return (
      <Alert color="red">
        Google API key is not configured. Please set
        NEXT_PUBLIC_GOOGLE_MAPS_API_KEY.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {!hideTitle && (
        <Text
          size="md"
          fw={700}
        >
          Verify Your Vote
        </Text>
      )}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
      >
        <Stack gap="md">
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                placeholder="Legal First Name"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                placeholder="Legal Last Name"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
              />
            </Grid.Col>
          </Grid>

          <PlaceAutocompleteElement
            apiKey={googleApiKey}
            defaultValue={formData.address}
            onPlaceSelected={(place) => {
              if (place) {
                // Get latitude and longitude
                const lat = place.geometry?.location?.lat();
                const lng = place.geometry?.location?.lng();

                // Extract state from address components
                let state = '';
                if (place.address_components) {
                  for (const component of place.address_components) {
                    if (
                      component.types.includes('administrative_area_level_1')
                    ) {
                      // Use the short_name for state code (e.g., "CA" instead of "California")
                      state = component.short_name;
                      break;
                    }
                  }
                }

                setFormData({
                  ...formData,
                  address: place.formatted_address || '',
                  state: state,
                  latitude: lat,
                  longitude: lng,
                });
                setIsAddressSelected(true);
              }
            }}
            onChange={(e) => {
              // Reset address selection when user types
              setIsAddressSelected(false);
              handleChange('address', (e.target as HTMLInputElement).value);
            }}
            options={{
              types: ['address'],
            }}
            placeholder="Search for an address"
            style={{
              width: '100%',
              height: '50px',
              padding: '10px 12px',
              borderRadius: 'var(--mantine-radius-md)',
              backgroundColor: 'var(--mantine-color-neutral-2)',
              color: 'var(--mantine-color-primary-9)',
              border: 'none',
              fontSize: '16px',
              outline: 'none',
            }}
            className={styles.placeAutocompleteInput}
          />

          {showDobField && (
            <TextInput
              placeholder="Date of Birth (MM/DD/YYYY)"
              value={formData.dob || ''}
              type="number"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                let formattedValue = value;

                // Add slashes as user types
                if (value.length > 2) {
                  formattedValue = value.slice(0, 2) + '/' + value.slice(2);
                }
                if (value.length > 4) {
                  formattedValue =
                    formattedValue.slice(0, 5) + '/' + formattedValue.slice(5);
                }

                // Limit to MM/DD/YYYY format
                if (formattedValue.length > 10) {
                  formattedValue = formattedValue.slice(0, 10);
                }

                handleChange('dob', formattedValue);
              }}
              description="Please enter your date of birth in MM/DD/YYYY format (e.g. 07/15/1985)"
            />
          )}

          {verificationResult?.isVerified && verificationResult.voterInfo && (
            <Alert variant="dimmed">
              <Text
                size="md"
                fw={700}
              >
                Verification Successful
              </Text>
              <Text size="md">
                You are verified as {verificationResult.voterInfo.firstName}{' '}
                {verificationResult.voterInfo.middleName
                  ? verificationResult.voterInfo.middleName + ' '
                  : ''}
                {verificationResult.voterInfo.lastName}{' '}
                {verificationResult.voterInfo.suffix || ''}
              </Text>
              <Text size="sm">
                Registration Status:{' '}
                {verificationResult.voterInfo.registrationStatus}
              </Text>
            </Alert>
          )}

          {verificationResult && !verificationResult.isVerified && (
            <Alert variant="dimmed">
              <Text
                size="md"
                fw={700}
              >
                Could not find voter registration
              </Text>
              <Text size="md">
                We couldn&apos;t verify your voter registration with the
                information provided. This might happen if your name, address,
                or birthday doesn&apos;t match the information on file with the
                state. Please add your date of birth or contact us here for
                help!
              </Text>
            </Alert>
          )}

          {error && <Warning error={error} />}

          <Group
            justify="flex-end"
            gap="md"
          >
            {onCancel && (
              <TrackedButton
                color="gray"
                size="lg"
                {...buttonSecondaryProps}
              />
            )}

            <TrackedButton
              fullWidth
              size="lg"
              visibleFrom="md"
              {...buttonPrimaryProps}
            />
          </Group>
          <Text size="md">
            Can&apos;t find your voter registration?{' '}
            <Anchor
              href="https://docs.google.com/forms/d/e/1FAIpQLSc1DaD4d26KoWAXaWLhE9dvRNk3yInoZpcfZ1en86K45W5WfA/viewform?usp=sharing"
              variant="helper"
            >
              Contact us here.
            </Anchor>
          </Text>
        </Stack>
      </form>
    </Stack>
  );
}
